import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  writeBatch,
  limit,
  updateDoc,
  Timestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';
import { logAuditEvent, AuditAction, auditOwnershipTransfer } from './audit';
import { User, getAuth } from 'firebase/auth';
import { checkSuperAdmin } from './permissions';
import { TeamMember } from '@/types';
import { initializeOnboarding } from './onboarding';

export interface Organization {
  name: string;
  slug: string;
  domainAutoJoin?: string;
  logoUrl?: string;
  iconUrl?: string; // Organization icon/avatar (smaller, for nav/lists)
  createdAt: Timestamp | ReturnType<typeof serverTimestamp>;
  deletedAt?: Timestamp | null; // Soft delete support
  settings?: {
    teamCreation?: 'admins' | 'managers' | 'members';
  };
  featureFlags?: {
    retrosEnabled?: boolean;
    critsEnabled?: boolean;
    snippetsEnabled?: boolean;
  };
}

export interface ProjectsViewPrefs {
  view: 'board' | 'table' | 'timeline';
  scope: 'mine' | 'team' | 'all';
  teamId?: string;
  states?: string[];
  labels?: string[];
  groupBy?: 'team' | 'person' | 'state' | 'label';
}

export interface Membership {
  role: 'owner' | 'admin' | 'manager' | 'member' | 'viewer';
  createdAt: Timestamp | ReturnType<typeof serverTimestamp>;
  deletedAt?: Timestamp | ReturnType<typeof serverTimestamp>;
  projectsViewPrefs?: ProjectsViewPrefs;
}

export async function createOrganization(
  uid: string,
  name: string,
  slug: string,
  domainAutoJoin?: string | null
) {
  // Check if slug is already taken
  const orgQuery = query(collection(db, 'orgs'), where('slug', '==', slug));
  const existingOrgs = await getDocs(orgQuery);
  
  if (!existingOrgs.empty) {
    throw new Error('Organization slug already exists');
  }

  const batch = writeBatch(db);
  const orgId = doc(collection(db, 'orgs')).id;
  
  // Create organization
  const orgRef = doc(db, 'orgs', orgId);
  const orgData: any = {
    name,
    slug,
    createdAt: serverTimestamp()
  };
  
  // Only add domainAutoJoin if it's defined and not null
  if (domainAutoJoin && domainAutoJoin !== null) {
    orgData.domainAutoJoin = domainAutoJoin;
  }
  
  batch.set(orgRef, orgData);
  
  // Add creator as owner
  const membershipRef = doc(db, 'orgs', orgId, 'memberships', uid);
  batch.set(membershipRef, {
    role: 'owner',
    createdAt: serverTimestamp()
  });
  
  // Add user profile to org
  const userRef = doc(db, 'orgs', orgId, 'users', uid);
  const globalUserRef = doc(db, 'users', uid);
  const globalUserDoc = await getDoc(globalUserRef);
  
  if (globalUserDoc.exists()) {
    batch.set(userRef, {
      ...globalUserDoc.data(),
      lastSeen: serverTimestamp()
    });
  }
  
  await batch.commit();

  // Verify the membership was created
  const verifyMembership = async (retries = 3): Promise<boolean> => {
    for (let i = 0; i < retries; i++) {
      const membershipCheck = await getDoc(doc(db, 'orgs', orgId, 'memberships', uid));
      if (membershipCheck.exists()) {
        return true;
      }
      // Wait a bit before retrying
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    return false;
  };

  await verifyMembership();

  // Initialize onboarding for the user
  try {
    await initializeOnboarding(uid);
  } catch (error) {
    console.error('Failed to initialize onboarding:', error);
    // Don't fail the org creation if onboarding initialization fails
  }

  return orgId;
}

export async function getAllOrganizations() {
  const orgsRef = collection(db, 'orgs');
  const orgsSnapshot = await getDocs(orgsRef);

  const allOrgs = [];

  for (const orgDoc of orgsSnapshot.docs) {
    const orgData = orgDoc.data();
    // Skip soft-deleted organizations
    if (orgData.deletedAt) {
      continue;
    }
    allOrgs.push({
      id: orgDoc.id,
      ...orgData
    });
  }

  return allOrgs;
}

export async function getUserOrganizations(uid: string, retryCount = 0): Promise<any[]> {
  const MAX_RETRIES = 4;
  const RETRY_DELAY_MS = 1000;

  try {

    // Check if user has super admin or debug mode access
    const auth = getAuth();
    const currentUser = auth.currentUser;

    let hasDebugAccess = false;
    let isSuperAdmin = false;

    if (currentUser) {
      const idTokenResult = await currentUser.getIdTokenResult();

      // Check for permanent super admin
      isSuperAdmin = idTokenResult.claims.superAdmin === true;

      // Check for temporary debug mode with valid expiry
      if (idTokenResult.claims.debugMode === true) {
        const debugExpires = idTokenResult.claims.debugExpires;
        if (debugExpires && typeof debugExpires === 'number' && debugExpires > Date.now()) {
          hasDebugAccess = true;
        }
      }
    }

    const orgsRef = collection(db, 'orgs');
    const orgsSnapshot = await getDocs(orgsRef);

    const userOrgs = [];

    for (const orgDoc of orgsSnapshot.docs) {
      const orgData = orgDoc.data();
      // Skip soft-deleted organizations
      if (orgData.deletedAt) {
        continue;
      }

      try {
        const membershipRef = doc(db, 'orgs', orgDoc.id, 'memberships', uid);
        const membershipDoc = await getDoc(membershipRef);

        if (membershipDoc.exists()) {
          userOrgs.push({
            id: orgDoc.id,
            ...orgData,
            role: membershipDoc.data().role
          });
        } else if (isSuperAdmin || hasDebugAccess) {
          // Super admins and debug mode users can see all orgs
          userOrgs.push({
            id: orgDoc.id,
            ...orgData,
            role: 'admin', // Give admin privileges
            isSuperAdminAccess: isSuperAdmin,
            isDebugAccess: hasDebugAccess
          });
        }
      } catch (orgError) {
        console.warn('[Organizations] Error checking membership for org', orgDoc.id, ':', orgError);
        // Continue to next org instead of failing
        continue;
      }
    }

    return userOrgs;
  } catch (error: any) {
    // Handle Firestore permission errors during auth state transition
    // This can happen when Firebase Auth has the user but the token hasn't propagated to Firestore yet
    const isPermissionError = error?.code === 'permission-denied' ||
      error?.message?.includes('Missing or insufficient permissions');

    if (isPermissionError && retryCount < MAX_RETRIES) {
      console.warn(`[Organizations] Permission error, retrying in ${RETRY_DELAY_MS}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`);
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
      return getUserOrganizations(uid, retryCount + 1);
    }

    // Re-throw if we've exhausted retries or it's a different error
    throw error;
  }
}

export async function getOrganizationBySlug(slug: string) {
  const orgQuery = query(collection(db, 'orgs'), where('slug', '==', slug), limit(1));
  const snapshot = await getDocs(orgQuery);
  
  if (snapshot.empty) {
    return null;
  }
  
  const doc = snapshot.docs[0];
  return {
    id: doc.id,
    ...doc.data()
  };
}

export async function addMemberToOrganization(
  orgId: string,
  uid: string,
  role: 'admin' | 'manager' | 'member' | 'viewer' = 'member'
) {
  const batch = writeBatch(db);

  // Add membership
  const membershipRef = doc(db, 'orgs', orgId, 'memberships', uid);
  batch.set(membershipRef, {
    role,
    createdAt: serverTimestamp()
  });

  // Add user profile to org
  const userRef = doc(db, 'orgs', orgId, 'users', uid);
  const globalUserRef = doc(db, 'users', uid);
  const globalUserDoc = await getDoc(globalUserRef);

  if (globalUserDoc.exists()) {
    batch.set(userRef, {
      ...globalUserDoc.data(),
      lastSeen: serverTimestamp()
    });
  }

  await batch.commit();

  // Initialize onboarding for users who can interact (not viewers)
  if (role !== 'viewer') {
    try {
      await initializeOnboarding(uid);
    } catch (error) {
      console.error('Failed to initialize onboarding:', error);
      // Don't fail the member addition if onboarding initialization fails
    }
  }
}

export async function checkUserMembership(orgId: string, uid: string) {
  const membershipRef = doc(db, 'orgs', orgId, 'memberships', uid);
  const membershipDoc = await getDoc(membershipRef);

  // If user has a real membership, always return that (takes priority over super admin)
  if (membershipDoc.exists()) {
    return membershipDoc.data() as Membership;
  }

  // Only use super admin access if user is NOT already a member
  // Check if user is super admin
  const auth = getAuth();
  const currentUser = auth.currentUser;
  if (currentUser && await checkSuperAdmin(currentUser)) {
    // Return admin membership for super admins to give them full org control
    return {
      role: 'admin', // Super admins get admin privileges
      createdAt: Timestamp.now(),
      isSuperAdminAccess: true // Flag to track this is super admin access
    } as Membership & { isSuperAdminAccess: boolean };
  }

  return null;
}

export async function getOrgMembers(orgId: string, excludeSuperAdmins: boolean = true): Promise<TeamMember[]> {
  const membershipsRef = collection(db, 'orgs', orgId, 'memberships');
  const snapshot = await getDocs(membershipsRef);

  const members = await Promise.all(
    snapshot.docs.map(async (membershipDoc): Promise<TeamMember | null> => {
      const membership = membershipDoc.data() as Membership;

      const uid = membershipDoc.id; // Document ID is the user's uid

      try {
        const userDoc = await getDoc(doc(db, 'users', uid));

        if (userDoc.exists()) {
          const userData = userDoc.data();

          // Check if this user is marked as a super admin and should be excluded
          // Super admins have a specific field or Firebase Auth claim
          if (excludeSuperAdmins) {
            // Check for super admin field in user document
            // This is set when a user is granted super admin status
            if (userData.isSuperAdmin === true) {
              return null; // Exclude super admin
            }
          }

          return {
            uid: uid,
            email: userData.email || null,
            displayName: userData.displayName || null,
            photoURL: userData.photoURL || null,
            role: membership.role
          };
        }
      } catch (error) {
        console.error('Error fetching user:', uid, error);
      }

      return null;
    })
  );

  return members.filter((m): m is TeamMember => m !== null);
}

export async function uploadOrgLogo(
  orgId: string,
  file: File
): Promise<string> {
  // Dynamically import browser-only library
  const imageCompression = (await import('browser-image-compression')).default;

  // Compress image
  const options = {
    maxSizeMB: 1,
    maxWidthOrHeight: 500,
    useWebWorker: true
  };

  const compressedFile = await imageCompression(file, options);

  // Upload to Firebase Storage
  const timestamp = Date.now();
  // Sanitize filename: remove special characters, keep only alphanumeric, dots, dashes, and underscores
  const sanitizedName = compressedFile.name
    .replace(/[^\w\s.-]/g, '') // Remove special characters except word chars, spaces, dots, dashes
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/\.+/g, '.') // Replace multiple dots with single dot
    .trim(); // Remove leading/trailing whitespace

  const fileName = `logo_${timestamp}_${sanitizedName}`;
  const storageRef = ref(storage, `orgs/${orgId}/logo/${fileName}`);
  
  await uploadBytes(storageRef, compressedFile);
  const downloadURL = await getDownloadURL(storageRef);
  
  return downloadURL;
}

export async function updateOrgLogo(
  orgId: string,
  logoUrl: string
) {
  const orgRef = doc(db, 'orgs', orgId);
  await updateDoc(orgRef, {
    logoUrl
  });
}

/**
 * Fetches organization icon from Logo.dev API based on domain
 * Returns the Logo.dev URL if available, null otherwise
 */
export function getLogoUrl(domain: string): string | null {
  if (!domain || domain.trim() === '') {
    return null;
  }

  // Clean the domain - remove http://, https://, www., and trailing slashes
  const cleanDomain = domain
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '')
    .split('/')[0]; // Get just the domain part

  if (!cleanDomain || !cleanDomain.includes('.')) {
    return null;
  }

  // Logo.dev API URL - requires publishable key
  const token = process.env.NEXT_PUBLIC_LOGO_DEV_TOKEN;
  if (!token) {
    console.warn('NEXT_PUBLIC_LOGO_DEV_TOKEN not set');
    return null;
  }

  return `https://img.logo.dev/${cleanDomain}?token=${token}`;
}

// Backwards compatibility alias
export const getClearbitLogoUrl = getLogoUrl;

export async function uploadOrgIcon(
  orgId: string,
  file: File
): Promise<string> {
  // Dynamically import browser-only library
  const imageCompression = (await import('browser-image-compression')).default;

  // Compress image - icons should be smaller
  const options = {
    maxSizeMB: 0.5,
    maxWidthOrHeight: 256,
    useWebWorker: true
  };

  const compressedFile = await imageCompression(file, options);

  // Convert to base64 for Cloud Function
  const arrayBuffer = await compressedFile.arrayBuffer();
  const base64 = Buffer.from(arrayBuffer).toString('base64');

  // Call Cloud Function to upload (handles auth and storage)
  const { httpsCallable } = await import('firebase/functions');
  const { functions } = await import('./firebase');
  const uploadOrgIconFn = httpsCallable(functions, 'uploadOrgIcon');

  const result = await uploadOrgIconFn({
    orgId,
    imageData: base64,
    contentType: compressedFile.type,
    fileName: compressedFile.name
  });

  const data = result.data as { success: boolean; iconUrl: string };
  return data.iconUrl;
}

export async function updateOrgIcon(
  orgId: string,
  iconUrl: string | null
) {
  const orgRef = doc(db, 'orgs', orgId);
  if (iconUrl === null) {
    // Remove icon
    await updateDoc(orgRef, {
      iconUrl: null
    });
  } else {
    await updateDoc(orgRef, {
      iconUrl
    });
  }
}

export async function deleteOrganization(
  orgId: string,
  deletedBy: User,
  soft = true
) {
  const orgRef = doc(db, 'orgs', orgId);

  if (soft) {
    // Get org details for audit
    const orgDoc = await getDoc(orgRef);
    const orgData = orgDoc.exists() ? orgDoc.data() : null;

    // Soft delete - mark as deleted
    await updateDoc(orgRef, {
      deletedAt: serverTimestamp()
    });

    // Audit log
    if (orgData) {
      await logAuditEvent(
        AuditAction.ORG_DELETED,
        {
          orgId,
          actorId: deletedBy.uid,
          actorEmail: deletedBy.email || undefined,
          actorName: deletedBy.displayName || undefined
        },
        {
          targetId: orgId,
          targetType: 'org',
          metadata: { orgName: orgData.name },
          description: `Deleted organization "${orgData.name}"`
        }
      );
    }
  } else {
    // Hard delete would require admin SDK
    throw new Error('Hard delete not supported from client');
  }
}

export async function transferOrganizationOwnership(
  orgId: string,
  newOwnerId: string,
  newOwnerEmail: string,
  transferredBy: User
) {
  const batch = writeBatch(db);

  // Get org details
  const orgDoc = await getDoc(doc(db, 'orgs', orgId));
  if (!orgDoc.exists()) {
    throw new Error('Organization not found');
  }
  const orgData = orgDoc.data();

  // Get current owner
  const membershipsRef = collection(db, 'orgs', orgId, 'memberships');
  const ownerQuery = query(membershipsRef, where('role', '==', 'owner'));
  const ownerSnapshot = await getDocs(ownerQuery);

  if (ownerSnapshot.empty) {
    throw new Error('No current owner found');
  }

  // Demote current owner to admin
  const currentOwnerId = ownerSnapshot.docs[0].id;
  batch.update(doc(db, 'orgs', orgId, 'memberships', currentOwnerId), {
    role: 'admin'
  });

  // Promote new owner
  const newOwnerMembershipRef = doc(db, 'orgs', orgId, 'memberships', newOwnerId);
  const newOwnerMembership = await getDoc(newOwnerMembershipRef);

  if (!newOwnerMembership.exists()) {
    throw new Error('New owner is not a member of this organization');
  }

  batch.update(newOwnerMembershipRef, {
    role: 'owner'
  });

  await batch.commit();

  // Audit log
  await auditOwnershipTransfer(
    {
      orgId,
      actorId: transferredBy.uid,
      actorEmail: transferredBy.email || undefined,
      actorName: transferredBy.displayName || undefined
    },
    'org',
    orgId,
    orgData.name,
    newOwnerId,
    newOwnerEmail
  );
}

export async function getProjectsViewPrefs(orgId: string, userId: string): Promise<ProjectsViewPrefs | null> {
  const membershipRef = doc(db, 'orgs', orgId, 'memberships', userId);
  const membershipDoc = await getDoc(membershipRef);
  if (!membershipDoc.exists()) return null;
  return membershipDoc.data()?.projectsViewPrefs || null;
}

export async function saveProjectsViewPrefs(orgId: string, userId: string, prefs: ProjectsViewPrefs): Promise<void> {
  const membershipRef = doc(db, 'orgs', orgId, 'memberships', userId);
  await updateDoc(membershipRef, { projectsViewPrefs: prefs });
}